// The primary pay explanation: a gross-to-net walk built entirely from the
// backend's category taxonomy (no country logic here — line names come from
// the payroll engine's country packs). Each deduction group expands to its
// line items with the engine's own explanations.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/features/shared/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CategoryGroup, ComponentType, PayslipDetail } from '../types';
import { CATEGORY_META, MASK, formatMoney } from '../lib/format';
import { pruneCategories } from './CategorySection';

// Deduction groups in walk order. Whether a group affects taxable income is
// structural metadata (pre-tax reduces it); the per-line names/amounts are data.
const DEDUCTION_ORDER: ComponentType[] = ['STATUTORY_TAX', 'PRE_TAX_DEDUCTION', 'POST_TAX_DEDUCTION'];

export const GrossToNetSnapshot: React.FC<{ detail: PayslipDetail; revealed: boolean }> = ({ detail, revealed }) => {
  const { t } = useTranslation('payroll');
  const money = (n: number) => (revealed ? formatMoney(n, detail.currency) : MASK);
  const groups = pruneCategories(detail.categories);
  const earning = groups.find((g) => g.type === 'EARNING');
  const deductions = DEDUCTION_ORDER.map((ty) => groups.find((g) => g.type === ty)).filter(
    (g): g is CategoryGroup => !!g,
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-xl border border-border" role="table" aria-label={t('myPay.grossToNetAria')}>
        <WalkRow
          label={t('myPay.grossEarnings')}
          amount={money(earning?.total ?? detail.gross)}
          sign="+"
          group={earning}
          money={money}
          strong
        />
        {deductions.map((g) => (
          <WalkRow
            key={g.type}
            label={t(CATEGORY_META[g.type].labelKey)}
            hint={g.type === 'PRE_TAX_DEDUCTION' ? t('myPay.preTaxHint') : g.type === 'POST_TAX_DEDUCTION' ? t('myPay.postTaxHint') : undefined}
            amount={money(g.total)}
            sign="-"
            group={g}
            money={money}
          />
        ))}
        <div className="flex items-center justify-between gap-2 rounded-b-xl border-t border-border bg-muted/40 px-4 py-3" role="row">
          <span className="text-sm font-semibold text-charcoal" role="rowheader">= {t('myPay.netPay')}</span>
          <span className="text-h3 tabular-nums text-[#166534] dark:text-[#4ade80]" role="cell">{money(detail.net)}</span>
        </div>
      </div>
    </TooltipProvider>
  );
};

const WalkRow: React.FC<{
  label: string;
  hint?: string;
  amount: string;
  sign: '+' | '-';
  group?: CategoryGroup;
  money: (n: number) => string;
  strong?: boolean;
}> = ({ label, hint, amount, sign, group, money, strong }) => {
  const [open, setOpen] = useState(false);
  const expandable = !!group && group.items.length > 0;
  const negative = sign === '-';

  return (
    <div className="border-b border-border last:border-b-0" role="row">
      <button
        type="button"
        disabled={!expandable}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={expandable ? open : undefined}
        className={cn('flex w-full items-center justify-between gap-2 px-4 py-3 text-left', expandable && 'hover:bg-muted/40')}
      >
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold',
              negative ? 'bg-[#FEE2E2] text-[#991B1B] dark:bg-[#991B1B]/25 dark:text-[#fca5a5]' : 'bg-[#DCFCE7] text-[#166534] dark:bg-[#166534]/25 dark:text-[#86efac]',
            )}
            aria-hidden
          >
            {sign}
          </span>
          <span className={cn('text-sm text-charcoal', strong && 'font-semibold')}>{label}</span>
          {hint && <span className="hidden text-[11px] text-muted-foreground sm:inline">{hint}</span>}
        </span>
        <span className="flex items-center gap-2">
          <span className={cn('text-sm font-medium tabular-nums', negative ? 'text-[#991B1B] dark:text-[#fca5a5]' : 'text-charcoal')}>
            {negative ? '−' : ''}{amount}
          </span>
          {expandable && (
            <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
          )}
        </span>
      </button>
      {expandable && open && (
        <ul className="space-y-1 px-4 pb-3 pl-11">
          {group!.items.map((it) => (
            <li key={it.code} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {it.label}
                {it.explanation && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground/70 hover:text-charcoal" aria-label={it.explanation}>
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">{it.explanation}</TooltipContent>
                  </Tooltip>
                )}
              </span>
              <span className={cn('tabular-nums', negative ? 'text-[#991B1B] dark:text-[#fca5a5]' : 'text-charcoal')}>
                {negative ? '−' : '+'}{money(it.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
